import { Grid } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumTipoSujeto } from '../../../../api'
import { ButtonOption } from '../../../../components/shared/splitButton/splitButton'
import { enumEnumToValueNumber } from '../../../../helpers'
import { useConfirmationDialog } from '../../../../hooks'
import { NonAuthRoutes } from '../../../../model'
import { InformationDialogs } from '../../../../model/enums/informationDialogs'
import {
  fetchClientCommercialsByID,
  fetchClientContractsByID,
  fetchClientDataByID
} from '../../../../redux'
import {
  selectClientData,
  selectIsFetchingClientData,
  selectIsFetchingClientDataError
} from '../../../../redux/selectors/client.selectors'
import { selectCompanyCommodities } from '../../../../redux/selectors/profile.selectors'
import { getClientName } from '../../client.helper'
import { useStyles } from '../client.file.styles'
import { ContractAddButtonOptions } from '../components/contractsCard/contractsCard.types'
import { InfoCard } from '../components/infoCard/infoCard'
import { TradesTable } from '../../../trades/components/tradesTable/TradesTable'
import { useNotify, SelectOption } from '@globaleris/ui.library'
import { useHttpError } from '../../../../hooks/useHttpError'
import { usePageState } from '../../../../hooks/usePageState'
import { useErrorDetailListDialog } from '../../../../hooks/useErrorDetailListDialog'
import { TradeListItemViewModel } from '../../../trades/trades.model'
import { fetchTrades } from '../../../trades/trades.service'
import {
  sortTradesByDateAndTime,
  extractDistinctLogisticElements
} from '../../../trades/trades.mapper'
import { TRADES_TABS } from '../../../trades'
export interface ClientFileMainProps {
  clientID: number
  // onShowAdditional: () => void
  // commodities: EnumCommodity[] | undefined
}

export const ClientFileTrades: React.FC<ClientFileMainProps> = ({ clientID }) => {
  const [t] = useTranslation(['globalerisLibrary', 'clients', 'clientFile', 'enums', 'trades'])
  const history = useHistory()
  const classes = useStyles()
  const { info } = useNotify()
  const { showError, errorProps } = useHttpError()
  const { dataTableFilter, dataTableSearch, dateFilter, customFilter } = usePageState()
  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()
  const { getInformation, closeInformation, getConfirmation } = useConfirmationDialog()

  // State
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [trades, setTrades] = useState<TradeListItemViewModel[] | undefined>(undefined)
  const [logisticElements, setLogisticElements] = useState<SelectOption[] | undefined>(undefined)
  const [searchFormDependencies, setSearchFormDependencies] = useState({
    deliveryPoints: [] as SelectOption[],
    counterparts: [] as SelectOption[],
    extendedClients: [] as SelectOption[]
  })

  const clientData = useSelector(selectClientData)

  const isFetchingClient = useSelector(selectIsFetchingClientData)
  const commodities = useSelector(selectCompanyCommodities)
  const fetchingClientError = useSelector(selectIsFetchingClientDataError)

  const getAddress = useCallback(() => {
    return clientData?.fiscalAddress
      ? `${clientData.fiscalAddress.postalAddress}, ${clientData.fiscalAddress.municipality}, ${clientData.fiscalAddress.province}, ${clientData.fiscalAddress.country}`
      : ''
  }, [clientData])

  const handleClickInfo = () => {
    history.push(NonAuthRoutes.ClientEdit, {
      clientID: clientID,
      subjectType: clientData?.type
        ? enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
        : EnumTipoSujeto.ClienteFinal
    })
  }

  const handleOnAddContact = () => {
    history.push(NonAuthRoutes.ContactInfo, {
      id: clientID,
      subjectType: clientData?.type ?? EnumTipoSujeto.ClienteFinal
    })
  }

  const handleOnAddContract = (selectedOption: ButtonOption) => {
    if (!clientData) {
      return
    }

    const subjectType = enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)

    switch (selectedOption.name) {
      case ContractAddButtonOptions.DEFAULT: {
        if (subjectType) {
          switch (subjectType) {
            case EnumTipoSujeto.Comercial:
              history.push(NonAuthRoutes.CommercialEdit, {
                commercialId: clientID,
                activeTabPanel: 'contracts'
              })
              break
            case EnumTipoSujeto.Mayorista:
              history.push(NonAuthRoutes.partnersEdit, {
                idCliente: clientID,
                activeTabPanel: 'contracts'
              })
              break
            default:
              history.push(NonAuthRoutes.clientsModEdit, {
                idCliente: clientID,
                activeTabPanel: 'contracts'
              })
          }
        }
        break
      }
      case ContractAddButtonOptions.CHANGE_OWNER:
        history.push(NonAuthRoutes.ContractChangeOwner)
        break
      case ContractAddButtonOptions.GAS:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
      case ContractAddButtonOptions.POWER:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
      case ContractAddButtonOptions.COMMERCIAL:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
    }
  }

  const handleOnEditContracts = () => {
    const subjectType = enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
    if (subjectType) {
      if (subjectType === EnumTipoSujeto.ClienteFinal) {
        history.push(NonAuthRoutes.clientsModEdit, {
          idCliente: clientID,
          activeTabPanel: 'contracts'
        })
      } else if (subjectType === EnumTipoSujeto.Mayorista) {
        history.push(NonAuthRoutes.partnersEdit, {
          idCliente: clientID,
          activeTabPanel: 'contracts'
        })
      } else if (subjectType === EnumTipoSujeto.Comercial) {
        history.push(NonAuthRoutes.CommercialEdit, {
          commercialId: clientID,
          activeTabPanel: 'contracts'
        })
      }
    } else {
      history.push(NonAuthRoutes.clientsModEdit, {
        idCliente: clientID,
        activeTabPanel: 'contracts'
      })
    }
  }

  const getContracts = async () => {
    fetchClientContractsByID(clientID)
  }

  const getClientData = () => {
    fetchClientDataByID(clientID)
  }

  const getCommercials = (clientID: number) => {
    fetchClientCommercialsByID(clientID)
  }

  // Mounting
  useEffect(() => {
    const showAnnouncement = async () => {
      await getInformation(InformationDialogs.COMMUNICATION_NEW_PAGE_CLIENT_FILE).then(res =>
        closeInformation()
      )
    }
    showAnnouncement()

    if (clientID) {
      if (!clientData || clientID !== clientData.id) {
        getClientData()
        getContracts()
        getCommercials(clientID)
      }
    } else {
      history.goBack()
    }
  }, [])

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item container xs={12} spacing={2} style={{ alignContent: 'flex-start' }}>
          <Grid item xs={12}>
            <TradesTable
              goBackTo={NonAuthRoutes.ClientFile}
              isFetching={isFetching}
              currentTab={TRADES_TABS.ALL}
              subjectID={clientID}
              errorProps={errorProps}
              dataTableFilter={dataTableFilter}
              dataTableSearch={dataTableSearch ?? ''}
              customFilter={customFilter}
              dateFilter={dateFilter}
              errorDetailListDialogProps={errorDetailListDialogProps}
              hideAddButton
            />
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}
